import { trackLoyaltyCreditCardApplyNowClick, type LoyaltyCreditCardApplyNowPlacement } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import TextButton from 'LuxKit/Button/TextButton'
import { useLoyaltyCreditCardApplyUrl } from 'loyalty/hooks/useLoyaltyCreditCardApplyUrl'
import { useCallback, useContext, type ComponentProps, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyCreditCardEmployeeEmailModal from './LoyaltyCreditCardEmployeeEmailModal'

const STAFF_EMAIL_DOMAINS = new Set(['luxuryescapes.com', 'luxgroup.com'])

interface Props {
  children?: ReactNode
  className?: string
  fit?: ComponentProps<typeof TextButton>['fit']
  placement: LoyaltyCreditCardApplyNowPlacement
  size?: ComponentProps<typeof TextButton>['size']
}

function LoyaltyCreditCardApplyNowButton({ children, className, fit, placement, size = 'medium' }: Props) {
  const intl = useIntl()
  const applyUrl = useLoyaltyCreditCardApplyUrl()
  const showModal = useContext(ModalContext)
  const email = useAppSelector(state => state.auth.account.email)
  const [emailLocalPart, emailDomain] = (email ?? '').trim().toLowerCase().split('@')
  const hasStaffEmailAlias = emailLocalPart.includes('+') && STAFF_EMAIL_DOMAINS.has(emailDomain)
  const href = hasStaffEmailAlias ? undefined : applyUrl
  const handleClick = useCallback(() => {
    trackLoyaltyCreditCardApplyNowClick(placement)
    if (hasStaffEmailAlias) {
      showModal(<LoyaltyCreditCardEmployeeEmailModal />)
    }
  }, [placement, hasStaffEmailAlias, showModal])

  return (
    <TextButton
      kind="primary"
      shape="round"
      size={size}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      fit={fit}
      onClick={handleClick}
    >
      {children ?? intl.formatMessage({
        id: 'opPvWiIiZ4hja1C1IaRO',
        defaultMessage: 'Apply now',
        description: 'Primary CTA on the Société Credit Card landing page',
      })}
    </TextButton>
  )
}

export default LoyaltyCreditCardApplyNowButton
